// Enhanced Skills Data Parser for VA Sprouts
// Handles various skill formats: legacy strings, up-skills format, and raw JSON recovery

/**
 * Safely parses and normalizes skills data from various formats
 * @param {any} rawSkills - Skills data in various formats (string, array, object)
 * @returns {Array} - Normalized array of skills
 */
export const parseSkillsData = (rawSkills) => {
  if (!rawSkills) return [];
  
  let parsedSkills = rawSkills;
  
  // Handle JSON string format
  if (typeof rawSkills === 'string') {
    try {
      parsedSkills = JSON.parse(rawSkills);
    } catch (error) {
      console.error('SkillsParser: Error parsing skills JSON:', error);
      return [];
    }
  }
  
  // Ensure it's an array
  if (!Array.isArray(parsedSkills)) {
    console.warn('SkillsParser: Skills data is not an array:', parsedSkills);
    return [];
  }
  
  return parsedSkills;
};

/**
 * Validates and cleans individual skill items, fixing raw JSON issues
 * @param {Array} skills - Array of skill items to validate
 * @returns {Object} - Object containing validatedSkills array and hasRawJsonIssues boolean
 */
export const validateAndCleanSkills = (skills) => {
  if (!Array.isArray(skills)) return { validatedSkills: [], hasRawJsonIssues: false };
  
  const validatedSkills = [];
  let hasRawJsonIssues = false;
  
  for (let index = 0; index < skills.length; index++) {
    const skill = skills[index];
    
    // Skip null/undefined
    if (skill === null || skill === undefined) {
      continue;
    }
    
    // Handle string skills (legacy format)
    if (typeof skill === 'string' && skill.trim()) {
      validatedSkills.push(skill.trim());
      continue;
    }
    
    // Handle object skills
    if (typeof skill === 'object' && skill !== null) {
      const hasValidName = (typeof skill.name === 'string' && skill.name.trim()) ||
                          (typeof skill.skill === 'string' && skill.skill.trim()) ||
                          (typeof skill.title === 'string' && skill.title.trim());
      
      if (hasValidName) {
        validatedSkills.push(skill);
        continue;
      }
      
      // Try to fix raw JSON issues
      hasRawJsonIssues = true;
      const fixedSkill = attemptRawJsonFix(skill);
      
      if (fixedSkill) {
        validatedSkills.push(fixedSkill);
      }
    }
  }
  
  return { validatedSkills, hasRawJsonIssues };
};

/**
 * Attempts to fix raw JSON skill objects by extracting meaningful data
 * @param {Object} skill - Raw skill object to fix
 * @returns {string|Object|null} - Fixed skill or null if unfixable
 */
const attemptRawJsonFix = (skill) => {
  const keys = Object.keys(skill);
  
  if (keys.length === 0) return null;
  
  // Check if this is a raw JSON object with name/category/rating structure
  if (typeof skill === 'object' && skill !== null) {
    // Check if it's a JSON representation of a skill object
    if (skill.name && typeof skill.name === 'string') {
      return {
        name: skill.name,
        category: typeof skill.category === 'string' ? skill.category : 'Other',
        rating: typeof skill.rating === 'number' ? skill.rating : null,
        level: typeof skill.level === 'string' ? skill.level : 'Intermediate',
        experience: typeof skill.experience === 'string' ? skill.experience : '',
        verified: Boolean(skill.verified)
      };
    }
  }
  
  // Look for nested data patterns
  for (const key of keys) {
    const value = skill[key];
    
    // Check if value is a stringified JSON
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null && (parsed.name || parsed.skill || parsed.title)) {
          return parsed;
        }
      } catch (e) {
        // Not JSON, continue
      }
    }
    
    // Check if value is already an object with skill data
    if (typeof value === 'object' && value !== null && (value.name || value.skill || value.title)) {
      return value;
    }
  }
  
  // Try to extract meaningful string data
  const stringValues = keys.filter(key => 
    typeof skill[key] === 'string' && 
    skill[key].trim() &&
    !skill[key].startsWith('{') && 
    !skill[key].startsWith('[') &&
    skill[key].length < 100 // Reasonable skill name length
  );
  
  if (stringValues.length > 0) {
    return skill[stringValues[0]].trim();
  }
  
  return null;
};

/**
 * Normalizes a single skill to a consistent format
 * @param {string|Object} skill - Skill to normalize
 * @param {number} index - Index for generating IDs
 * @returns {Object} - Normalized skill object
 */
export const normalizeSkill = (skill, index = 0) => {
  // Handle string skills
  if (typeof skill === 'string') {
    // Check if the string is a JSON string
    if (skill.trim().startsWith('{') && skill.trim().endsWith('}')) {
      try {
        const parsedSkill = JSON.parse(skill);
        // Recursively normalize the parsed skill
        return normalizeSkill(parsedSkill, index);
      } catch (e) {
        // If parsing fails, treat as a regular string skill
        console.warn('Failed to parse JSON string skill:', e);
      }
    }
    
    return {
      id: `skill-${index}-${skill.replace(/\W/g, '').toLowerCase()}`,
      name: skill,
      type: 'legacy',
      wasRawJson: false
    };
  }
  
  // Handle object skills
  if (typeof skill === 'object' && skill !== null) {
    let skillName = 'Unknown Skill';
    let wasRawJson = false;
    let parsedFromJson = false;
    
    // Extract name
    if (typeof skill.name === 'string') {
      // Check if name is a JSON string
      if (skill.name.trim().startsWith('{') && skill.name.trim().endsWith('}')) {
        try {
          const parsedName = JSON.parse(skill.name);
          if (typeof parsedName === 'object' && parsedName !== null) {
            // If the name is a valid JSON object, use it as the skill object
            parsedFromJson = true;
            // Recursively normalize with the parsed object
            const normalizedFromJson = normalizeSkill(parsedName, index);
            // Keep the original ID
            normalizedFromJson.id = skill.id || normalizedFromJson.id;
            return normalizedFromJson;
          }
        } catch (e) {
          // If parsing fails, continue with the string as is
          console.warn('Failed to parse JSON in skill name:', e);
        }
      }
      
      skillName = skill.name.trim();
    } else if (typeof skill.skill === 'string' && skill.skill.trim()) {
      skillName = skill.skill.trim();
    } else if (typeof skill.title === 'string' && skill.title.trim()) {
      skillName = skill.title.trim();
    } else {
      // This indicates it was likely recovered from raw JSON
      wasRawJson = true;
      // Try to extract a name from the object
      const keys = Object.keys(skill);
      for (const key of keys) {
        if (typeof key === 'string' && key.toLowerCase().includes('name')) {
          if (typeof skill[key] === 'string' && skill[key].trim()) {
            skillName = skill[key].trim();
            break;
          }
        }
      }
    }
    
    // Ensure category is a string
    let category = 'Other';
    if (typeof skill.category === 'string') {
      category = skill.category;
    } else if (skill.category && typeof skill.category === 'object') {
      // If category is an object, try to extract a string value
      if (typeof skill.category.name === 'string') {
        category = skill.category.name;
      } else {
        const catKeys = Object.keys(skill.category);
        for (const key of catKeys) {
          if (typeof skill.category[key] === 'string') {
            category = skill.category[key];
            break;
          }
        }
      }
    }
    
    return {
      id: skill.id || `skill-${index}-${skillName.replace(/\W/g, '').toLowerCase()}`,
      name: skillName,
      category: category,
      level: typeof skill.level === 'string' ? skill.level : 
             typeof skill.difficulty_level === 'string' ? skill.difficulty_level : 'Intermediate',
      rating: typeof skill.rating === 'number' ? skill.rating : null,
      experience: typeof skill.experience === 'string' ? skill.experience : '',
      verified: Boolean(skill.verified),
      yearsOfExperience: typeof skill.yearsOfExperience === 'number' ? skill.yearsOfExperience : 
                        typeof skill.experience_years === 'number' ? skill.experience_years : 0,
      type: skill.rating ? 'upskills' : 'structured',
      wasRawJson: wasRawJson || parsedFromJson
    };
  }
  
  // Fallback
  return {
    id: `unknown-${index}`,
    name: 'Unknown Skill',
    category: 'Other',
    level: 'Beginner',
    type: 'unknown',
    wasRawJson: true
  };
};

/**
 * Completely processes raw skills data into normalized format
 * @param {any} rawSkills - Raw skills data
 * @returns {Object} - Object containing processed skills and metadata
 */
export const processSkillsData = (rawSkills) => {
  // Parse the raw data
  const parsedSkills = parseSkillsData(rawSkills);
  
  // Validate and clean
  const { validatedSkills, hasRawJsonIssues } = validateAndCleanSkills(parsedSkills);
  
  // Normalize all skills
  const normalizedSkills = validatedSkills.map((skill, index) => normalizeSkill(skill, index));
  
  // Calculate statistics
  const stats = {
    total: normalizedSkills.length,
    legacy: normalizedSkills.filter(s => s.type === 'legacy').length,
    upskills: normalizedSkills.filter(s => s.type === 'upskills').length,
    structured: normalizedSkills.filter(s => s.type === 'structured').length,
    recoveredFromRawJson: normalizedSkills.filter(s => s.wasRawJson).length,
    withRating: normalizedSkills.filter(s => s.rating && s.rating > 0).length,
    withExperience: normalizedSkills.filter(s => s.experience && s.experience.trim()).length,
    verified: normalizedSkills.filter(s => s.verified).length
  };
  
  return {
    skills: normalizedSkills,
    stats,
    hasRawJsonIssues,
    needsSync: hasRawJsonIssues && normalizedSkills.length > 0
  };
};

/**
 * Safely extracts skill name from various skill formats
 * @param {string|Object} skill - Skill to extract name from
 * @returns {string} - Safe skill name
 */
export const getSafeSkillName = (skill) => {
  if (!skill) return 'Unknown Skill';
  
  // For string skills, check if it's a JSON string first
  if (typeof skill === 'string') {
    if (skill.trim().startsWith('{') && skill.trim().endsWith('}')) {
      try {
        const parsed = JSON.parse(skill);
        return getSafeSkillName(parsed); // Recursively process parsed object
      } catch (e) {
        // Not valid JSON, return as is
        return skill;
      }
    }
    return skill;
  }
  
  // Handle object skills
  if (typeof skill === 'object' && skill !== null) {
    // Check standard name properties
    if (typeof skill.name === 'string') {
      // Check if name is a JSON string
      if (skill.name.trim().startsWith('{') && skill.name.trim().endsWith('}')) {
        try {
          const parsedName = JSON.parse(skill.name);
          if (typeof parsedName === 'object' && parsedName !== null) {
            // If name is a valid JSON object with a name property
            if (typeof parsedName.name === 'string' && parsedName.name.trim()) {
              return parsedName.name.trim();
            }
            // Try other properties in the parsed object
            return getSafeSkillName(parsedName);
          }
        } catch (e) {
          // If parsing fails, continue with the string as is
        }
      }
      return skill.name.trim();
    }
    
    if (typeof skill.skill === 'string' && skill.skill.trim()) return skill.skill.trim();
    if (typeof skill.title === 'string' && skill.title.trim()) return skill.title.trim();
    
    // Handle nested name objects
    if (skill.name && typeof skill.name === 'object') {
      return getSafeSkillName(skill.name); // Recursively process nested name object
    }
    
    // Try to find any property that might contain a name
    const keys = Object.keys(skill);
    for (const key of keys) {
      if (typeof key === 'string' && key.toLowerCase().includes('name')) {
        if (typeof skill[key] === 'string') {
          // Check if this string property is a JSON string
          if (skill[key].trim().startsWith('{') && skill[key].trim().endsWith('}')) {
            try {
              const parsedValue = JSON.parse(skill[key]);
              const nestedName = getSafeSkillName(parsedValue);
              if (nestedName !== 'Unknown Skill') {
                return nestedName;
              }
            } catch (e) {
              // If parsing fails, use the string as is
              return skill[key].trim();
            }
          } else {
            return skill[key].trim();
          }
        } else if (typeof skill[key] === 'object' && skill[key] !== null) {
          // Try to extract name from nested object
          const nestedName = getSafeSkillName(skill[key]);
          if (nestedName !== 'Unknown Skill') {
            return nestedName;
          }
        }
      }
    }
    
    // Check for category.name pattern (common in API responses)
    if (skill.category && typeof skill.category === 'object' && typeof skill.category.name === 'string') {
      return skill.category.name.trim();
    }
    
    // If we still don't have a name, try to use any string property
    for (const key of keys) {
      if (typeof skill[key] === 'string' && skill[key].trim()) {
        // Check if this string property is a JSON string
        if (skill[key].trim().startsWith('{') && skill[key].trim().endsWith('}')) {
          try {
            const parsedValue = JSON.parse(skill[key]);
            const nestedName = getSafeSkillName(parsedValue);
            if (nestedName !== 'Unknown Skill') {
              return nestedName;
            }
          } catch (e) {
            // If parsing fails, use the string if it's not too complex
            if (!skill[key].includes('{') || skill[key].length < 50) {
              return skill[key].trim();
            }
          }
        } else if (!skill[key].startsWith('[')) {
          return skill[key].trim();
        }
      }
    }
    
    // Last resort: If the object has a toString method that doesn't return [object Object]
    const stringified = String(skill);
    if (stringified && stringified !== '[object Object]') {
      return stringified;
    }
  }
  
  return 'Unknown Skill';
};

/**
 * Safely extracts skill experience from skill object
 * @param {Object} skill - Skill object
 * @returns {string} - Safe experience description
 */
export const getSafeSkillExperience = (skill) => {
  if (!skill) return 'No experience description available.';
  
  if (typeof skill.experience === 'string' && skill.experience.trim()) {
    return skill.experience.trim();
  }
  
  return 'No experience description available.';
};